import * as DialogPrimitive from "@radix-ui/react-dialog";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDownUp,
  Building2,
  Flame,
  Globe2,
  Layers,
  Truck,
  X,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import OrgBreadcrumb, {
  useOrgPathWithStatus,
} from "@/components/common/OrgBreadcrumb";
import { useTran } from "@/hooks";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { cn } from "@/lib/utils";
import {
  getUnitTypeMeta,
  ORGANIZATION_TYPE_META,
} from "@/features/organization/utils/organization.helpers";
import {
  formatEmissionValue,
  formatPercentage,
} from "../../../domains/formatting";
import {
  getEmissionsBreakdownMetricTitle,
  getEmissionsBreakdownTitle,
} from "../../../domains/emissions-breakdown";
import { loadEmissionsBreakdown } from "../../../operations/load-emissions-breakdown.ops";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { dashboardEmissionsBreakdownStore } from "../../../stores/emissions-breakdown.store";
import { dashboardFiltersStore } from "../../../stores/filters.store";

const SCOPE_ICON = {
  all: Layers,
  1: Flame,
  2: Zap,
  3: Truck,
};

const SCOPE_ICON_TONE = {
  all: "bg-emerald-500/10 text-emerald-600",
  1: "bg-blue-500/10 text-blue-600",
  2: "bg-amber-500/10 text-amber-600",
  3: "bg-violet-500/10 text-violet-600",
};
const VALUE_SORT_ASC = "asc";
const VALUE_SORT_DESC = "desc";
const EMPTY_ROWS = [];

const compareRowsByValue = (direction) => (left, right) => {
  const leftValue = Number(left?.value);
  const rightValue = Number(right?.value);
  const leftFinite = Number.isFinite(leftValue);
  const rightFinite = Number.isFinite(rightValue);

  if (!leftFinite && !rightFinite) {
    return String(left?.organizationName || "").localeCompare(
      String(right?.organizationName || ""),
    );
  }
  if (!leftFinite) return 1;
  if (!rightFinite) return -1;
  if (leftValue !== rightValue) {
    return direction === VALUE_SORT_ASC
      ? leftValue - rightValue
      : rightValue - leftValue;
  }
  return String(left?.organizationName || "").localeCompare(
    String(right?.organizationName || ""),
  );
};

const headerHint = ({ scope, hasSelection, t }) => {
  if (hasSelection) {
    return t("Showing only the selected organizations.");
  }
  if (String(scope) === "all") {
    return t(
      "Combined Scope 1, Scope 2, and Scope 3 emissions across the selected organization hierarchy.",
    );
  }
  return t("Showing all organizations in the selected company hierarchy.");
};

const getOrganizationTypeLabel = (unitLevel, organizationPath) => {
  if (Array.isArray(organizationPath) && organizationPath.length === 1) {
    return "Parent Company";
  }

  if (unitLevel === "level_2" || unitLevel === "company") {
    return "Company";
  }

  if (unitLevel === "parent_company") {
    return "Parent Company";
  }

  return getUnitTypeMeta(unitLevel).label || unitLevel || "Organization";
};

const EmptyState = ({ title, description }) => (
  <div className="rounded-lg border border-dashed border-border/70 bg-muted px-4 py-8 text-center">
    <p className="text-sm font-semibold text-foreground">{title}</p>
    <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
      {description}
    </p>
  </div>
);

const LoadingState = () => (
  <div className="space-y-2.5">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-lg border border-border/70 bg-card p-3 shadow-sm"
      >
        <div className="flex items-start gap-2.5">
          <div className="h-9 w-9 rounded-md bg-muted/60" />
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="h-3.5 w-2/5 rounded-full bg-muted/60" />
            <div className="h-3 w-1/3 rounded-full bg-muted/60" />
            <div className="h-1.5 w-full rounded-full bg-muted/60" />
          </div>
          <div className="h-6 w-14 rounded-full bg-muted/60" />
        </div>
      </div>
    ))}
  </div>
);

const BreakdownRow = ({ row, t, orgTree }) => {
  const meta =
    row.organizationUnitLevel === "country"
      ? ORGANIZATION_TYPE_META.country
      : getUnitTypeMeta(row.organizationUnitLevel);
  const Icon = meta.icon || Building2;
  const hierarchyLabel = Array.isArray(row.organizationPath)
    ? row.organizationPath.join(" > ")
    : "";
  const countryLabel =
    row.country &&
    (row.organizationUnitLevel !== "country" ||
      row.country.trim() !== row.organizationName?.trim())
      ? row.country
      : "";
  const hasPercentage = Number.isFinite(row.percentage);

  const { pathWithStatus, orgStatus, orgInactiveDate } = useOrgPathWithStatus(
    row.organizationId,
    orgTree,
  );
  const percentage = hasPercentage ? row.percentage : 0;

  return (
    <div
      className={cn(
        "w-full rounded-lg border border-border/70 bg-card p-3 text-left shadow-sm",
        row.hasChildren && "border-border",
      )}
    >
      <div
        className="flex items-start gap-2.5"
        // style={{ paddingLeft: `${Math.max(depth, 0) * 14}px` }}
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border ${meta.color}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-1.5 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-sm font-semibold text-foreground">
                  {row.organizationName}
                </p>
                <OrgStatusTag
                  status={orgStatus}
                  inactiveDate={orgInactiveDate}
                  className="text-[10px] shrink-0"
                />
              </div>
              <OrgBreadcrumb
                pathWithStatus={pathWithStatus}
                fallbackLabel={hierarchyLabel}
                className="mt-0.5"
              />
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground shadow-sm">
                  <span className="max-w-[170px] truncate">
                    {t(
                      getOrganizationTypeLabel(
                        row.organizationUnitLevel,
                        row.organizationPath,
                      ),
                    )}
                  </span>
                </span>
                {countryLabel ? (
                  <Badge
                    variant="secondary"
                    className="inline-flex items-center gap-1 text-[11px] font-normal"
                  >
                    <Globe2 className="h-3 w-3" />
                    {countryLabel}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-2 md:items-end">
              <div className="text-left md:text-right">
                <p className="text-xl font-semibold tracking-tight text-foreground">
                  {formatEmissionValue(row.value)}
                </p>
                <p className="text-[11px] text-muted-foreground">{row.unit}</p>
              </div>
            </div>
          </div>

          {hasPercentage ? (
            <div className="mt-3">
              <div className="h-1.5 rounded-full bg-muted/70">
                <div
                  className="h-1.5 rounded-full bg-[hsl(var(--chart-1))] transition-all"
                  style={{
                    width: `${Math.max(0, Math.min(percentage, 100))}%`,
                  }}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{t("Contribution")}</span>
                <span>{formatPercentage(percentage)}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const EmissionsBreakdownDrawer = observer(() => {
  const { t } = useTran();
  const store = dashboardEmissionsBreakdownStore;
  const [valueSortDirection, setValueSortDirection] = useState(VALUE_SORT_DESC);
  const selectedYear = dashboardEChartsStore.selectedYear;
  const framework = dashboardFiltersStore.filters.frameworks?.[0] || "";
  const orgTree = dashboardFiltersStore.available.orgTree || [];
  const reportingYears = dashboardFiltersStore.available.reportingYears;
  const selectedReportingYearLabel = useMemo(() => {
    if (!Number.isFinite(selectedYear)) return "";
    const reportingYear = (
      Array.isArray(reportingYears) ? reportingYears : []
    ).find((item) => Number(item?.startYear) === Number(selectedYear));
    return reportingYear
      ? formatReportingYearPeriod(reportingYear)
      : String(selectedYear);
  }, [reportingYears, selectedYear]);
  const scopeIconKey = String(store.scope);
  const ScopeIcon = SCOPE_ICON[scopeIconKey] || Layers;
  const scopeIconTone = SCOPE_ICON_TONE[scopeIconKey] || SCOPE_ICON_TONE.all;
  const title = t(getEmissionsBreakdownTitle(store.scope));
  const metricTitle = t(getEmissionsBreakdownMetricTitle(store.scope));
  const selectedOrgIdsKey = store.selectedOrgIds.join(",");

  useEffect(() => {
    if (!store.open) return;
    loadEmissionsBreakdown();
  }, [framework, selectedOrgIdsKey, selectedYear, store.open, store.scope]);

  const warnings = store.result?.warnings || [];
  const missingYear = warnings.some(
    (warning) => warning.code === "MISSING_YEAR",
  );
  const headline = store.result?.headline;
  const rows = store.result?.rows || EMPTY_ROWS;
  const sortedRows = useMemo(
    () => [...rows].sort(compareRowsByValue(valueSortDirection)),
    [rows, valueSortDirection],
  );
  const isValueSortDesc = valueSortDirection === VALUE_SORT_DESC;
  const canGoBack = store.selectedOrgIds.length > 0;
  const hasNestedRows = rows.some((row) => row.hasChildren);

  return (
    <DialogPrimitive.Root
      open={store.open}
      onOpenChange={(open) => {
        if (!open) store.close();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="fixed inset-y-0 right-0 z-[80] flex h-[100dvh] w-full max-w-[min(100vw,720px)] flex-col overflow-hidden border-l border-border/70 bg-background shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right-8 data-[state=open]:duration-300 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-200"
        >
          <div className="sticky top-0 z-20 border-b border-border/70 bg-background px-4 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <DialogPrimitive.Title className="text-base font-semibold tracking-tight text-foreground">
                    {title}
                  </DialogPrimitive.Title>
                  {Number.isFinite(selectedYear) ? (
                    <Badge variant="secondary">
                      {selectedReportingYearLabel}
                    </Badge>
                  ) : null}
                </div>
                <DialogPrimitive.Description className="mt-1 text-xs leading-5 text-muted-foreground">
                  {headerHint({
                    scope: store.scope,
                    hasSelection: canGoBack,
                    t,
                  })}
                </DialogPrimitive.Description>
              </div>

              <DialogPrimitive.Close className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground shadow-sm transition hover:border-border hover:text-foreground">
                <X className="h-5 w-5" />
                <span className="sr-only">{t("Close")}</span>
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-3">
              <div className="rounded-lg border border-border/70 bg-card p-3 shadow-sm">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("Organizations")}
                    </label>
                    <OrgTreeSelect
                      tree={orgTree}
                      value={store.selectedOrgIds}
                      onChange={store.setSelectedOrgIds}
                      multiSelect
                      showBranchSelectAll
                      branchSelectLevels={["parent_company", "level_1"]}
                      placeholder={t("All Organizations")}
                      helperText={t(
                        "Choose one or more organizations. Empty means every organization in scope.",
                      )}
                      disablePortal
                    />
                  </div>

                  {/* {canGoBack ? (
                    <div className="flex justify-start lg:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => store.setSelectedOrgIds([])}
                        className="rounded-full"
                      >
                        {t("Show all organizations")}
                      </Button>
                    </div>
                  ) : null} */}
                </div>
              </div>

              {store.error ? (
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span>{store.error}</span>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => loadEmissionsBreakdown()}
                      className="rounded-full border-destructive/30 bg-background text-destructive hover:border-destructive/40 hover:text-destructive"
                    >
                      {t("Try again")}
                    </Button>
                  </div>
                </div>
              ) : null}

              {missingYear ? (
                <EmptyState
                  title={t("Select a year to explore the breakdown")}
                  description={t(
                    "Pick a year from the KPI strip first, then open the emissions breakdown again.",
                  )}
                />
              ) : store.loading ? (
                <LoadingState />
              ) : !headline ? (
                <EmptyState
                  title={t("No emissions breakdown available")}
                  description={t(
                    "No emissions were found for the selected scope, year, framework, and organizations.",
                  )}
                />
              ) : (
                <>
                  <div className="rounded-lg border border-border/70 bg-card p-3 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <span
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                            scopeIconTone,
                          )}
                        >
                          <ScopeIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold text-foreground">
                            {metricTitle}
                          </p>
                          <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                            {canGoBack
                              ? t("Filtered by selected organizations")
                              : t("All organizations in scope")}
                          </p>
                        </div>
                      </div>

                      <div className="text-left lg:text-right">
                        <p className="text-2xl font-semibold tracking-tight text-foreground">
                          {formatEmissionValue(headline.value)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {headline.unit} - {title}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[12px] font-bold uppercase tracking-wide">
                          {t("Contribution by organizations")}
                        </p>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {canGoBack
                            ? t("Only the selected organizations are listed.")
                            : hasNestedRows
                              ? t(
                                  "Showing all organization levels, including business units, companies, and sites.",
                                )
                              : t("Current organization contribution ranking.")}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {rows.length > 1 ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                              setValueSortDirection((current) =>
                                current === VALUE_SORT_DESC
                                  ? VALUE_SORT_ASC
                                  : VALUE_SORT_DESC,
                              )
                            }
                            className="h-8 rounded-full px-2.5 text-[11px]"
                            title={
                              isValueSortDesc
                                ? t("High to low")
                                : t("Low to high")
                            }
                          >
                            <ArrowDownUp className="mr-1.5 h-3.5 w-3.5" />
                            {isValueSortDesc
                              ? t("High to low")
                              : t("Low to high")}
                          </Button>
                        ) : null}
                        <Badge variant="secondary">
                          {rows.length} {t("organizations")}
                        </Badge>
                      </div>
                    </div>

                    {rows.length === 0 ? (
                      <EmptyState
                        title={t("No organizations match this selection")}
                        description={t(
                          "Try a different organization selection or choose a higher-level organization to see child contributions.",
                        )}
                      />
                    ) : (
                      <div className="space-y-3">
                        {sortedRows.map((row) => (
                          <BreakdownRow
                            key={`${row.organizationId}-${row.depth || 0}`}
                            row={row}
                            t={t}
                            orgTree={orgTree}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});

export default EmissionsBreakdownDrawer;
